import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { usePhoneField } from '@/hooks/usePhoneField'
import { CacheKeys } from '@/lib/cache'
import { type OrgMember, type OrgMemberDetails, OrgRole, updateOrgMember } from '@/models/user'
import { OrgMemberFields } from './OrgMemberFields'

/** Staff edit of a member's name, contact details and role in one org. */
export function EditOrgMemberDialog({
  orgId,
  member,
  open,
  onOpenChange,
}: {
  orgId: string
  member: OrgMember
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [details, setDetails] = useState<OrgMemberDetails>({
    firstName: member.firstName,
    lastName: member.lastName,
    email: member.email,
    mobile: member.mobile ?? '',
    role: member.orgUser.role as OrgRole,
  })
  const queryClient = useQueryClient()
  const phoneField = usePhoneField()

  const mutation = useMutation({
    mutationFn: () => updateOrgMember(orgId, member.id, details),
    onSuccess: () => {
      toast.success('User updated')
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId] })
      onOpenChange(false)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Failed to update user'
      toast.error(message)
    },
  })

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!phoneField.check(details.mobile)) return
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>A new email or phone number has to be verified again.</DialogDescription>
          </DialogHeader>
          <OrgMemberFields
            idPrefix="edit-member"
            details={details}
            onChange={setDetails}
            phoneError={phoneField.error}
          />
          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !details.firstName || !details.lastName || !details.email}
              loading={mutation.isPending ? 'Saving…' : undefined}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
